import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  keyframes,
} from "@mui/material";
import { PiHandsClappingDuotone } from "react-icons/pi";
import {
  IconBellOff,
  IconHeartHandshake,
  IconMoodCheck,
} from "@tabler/icons-react";
import { LuBicepsFlexed } from "react-icons/lu";
import { cyan, lightGreen } from "@mui/material/colors";

import Notification from "data/firestore/notification";
import CurrentSession from "data/userSession";
import useCustomSnackbar from "hooks/customSnackbar";
import Logger from "utils/logger";
import AnalyticsService from "data/firestore/analytics";
import { NOTIFICATION_TYPES } from "../../config/notifications";

// ============================================================================
// Encourage/Kudos Logic
// ============================================================================

type InteractionType = "kudos" | "reminder" | "nudge";

interface UserStreakInfo {
  streak?: number;
  streakInDanger?: boolean;
  active?: boolean;
}

/**
 * Determine the interaction type based on user's streak status
 */
function getInteractionTypeForUser(user: UserStreakInfo): InteractionType {
  // Tugging right now -> give Kudos, regardless of streak. Otherwise someone
  // returning after months (streak 0) wrongly gets "Encourage" while mid-session.
  if (user.active) {
    return "kudos";
  } else if (!user.streak || user.streak <= 0) {
    return "nudge";
  } else if (user.streakInDanger) {
    return "reminder";
  } else {
    return "kudos";
  }
}

/**
 * Get the notification type string for a given interaction type
 */
function getNotificationType(interactionType: InteractionType): string {
  return interactionType === "kudos" || interactionType === "reminder"
    ? "KUDOS"
    : "NUDGE";
}

/**
 * Get button text based on interaction state
 */
function getEncourageButtonText(
  interactionType: InteractionType,
  hasSentToday: boolean,
  notificationsDisabled: boolean
): string {
  if (notificationsDisabled) {
    return "Opted Out";
  }

  if (hasSentToday) {
    return "Sent!";
  }

  switch (interactionType) {
    case "kudos":
    case "reminder":
      return "Give Kudos";
    case "nudge":
      return "Encourage";
    default:
      return "Encourage";
  }
}

/**
 * Get toast message for sent notification
 */
function getEncourageToastMessage(
  interactionType: InteractionType,
  userName: string
): string {
  const name = userName || "Restorer";

  switch (interactionType) {
    case "kudos":
    case "reminder":
      return `Kudos sent to ${name}!`;
    case "nudge":
      return `Nudge sent to ${name}!`;
    default:
      return `Interaction sent to ${name}!`;
  }
}

/**
 * Get dialog title based on interaction type
 */
function getEncourageDialogTitle(interactionType: InteractionType): string {
  return interactionType === "kudos" || interactionType === "reminder"
    ? "Send Kudos"
    : "Send Encouragement";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Gatekeep: any = require("../../components/Gatekeep").default;

export interface UserData {
  id?: string;
  name?: string;
  username?: {
    display?: string;
    standardized?: string;
  };
  above18?: boolean;
  streak?: number;
  streakInDanger?: boolean;
  active?: boolean;
  notificationsDisabled?: boolean;
  incognito?: boolean;
  preferences?: {
    notifications_kudos?: boolean;
  };
}

export interface KudosProps {
  user: UserData;
  isCurrentUser?: boolean;
  currentUserAbove18?: boolean;
  onKudosSent?: (sent: boolean) => void;
  compact?: boolean;
  active?: boolean;
}

const Kudos = ({
  user,
  isCurrentUser = false,
  currentUserAbove18 = true,
  onKudosSent,
  compact = false,
  active = false,
}: KudosProps) => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const [isInteracting, setIsInteracting] = useState(false);
  const [hasSentToday, setHasSentToday] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [notificationsDisabled, setNotificationsDisabled] = useState(false);

  // Gentle pulse animation for the active (pulsing biceps) state
  const pulseAnimation = keyframes`
    0% { opacity: .9; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
    100% { opacity: .9; transform: scale(1); }
  `;

  useEffect(() => {
    setNotificationsDisabled(!!user.incognito);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    const meId = CurrentSession.user?.id;
    const toId = user.id;
    if (!meId || !toId) return;
    let cancelled = false;
    // One shared "who did I send to today" query across all cards (memoized),
    // instead of a getDoc per card. Fall back to the direct per-pair check if the
    // batched query is unavailable (e.g. blocked by rules) so nothing regresses.
    Notification.sentToday(meId)
      .then((set: Set<string>) => {
        if (!cancelled) setHasSentToday(set.has(toId));
      })
      .catch(() => {
        Notification.checkIfSent(meId, toId).then((sent: boolean) => {
          if (!cancelled) setHasSentToday(sent);
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, isCurrentUser]);

  // Don't show interact button for current user
  if (isCurrentUser) return null;

  // Determine interaction type based on user's streak status
  const getInteractionType = () => {
    return getInteractionTypeForUser(user);
  };

  const getIcon = (size = 24) => {
    if (hasSentToday) {
      return <IconMoodCheck size={size} />;
    }
    if (active) {
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
            width: size,
            height: size,
            justifyContent: "center",
          }}
        >
          <LuBicepsFlexed size={size} color={cyan.A700} />
        </Box>
      );
    }
    if (notificationsDisabled) {
      return <IconBellOff size={size} />;
    }
    const type = getInteractionType();
    switch (type) {
      case "kudos":
      case "reminder":
        return <PiHandsClappingDuotone size={size} />;
      case "nudge":
        return <IconHeartHandshake size={size} />;
      default:
        return <IconHeartHandshake size={size} />;
    }
  };

  const getButtonText = () => {
    return getEncourageButtonText(
      getInteractionType(),
      hasSentToday,
      notificationsDisabled
    );
  };

  const getToastMessage = () => {
    return getEncourageToastMessage(
      getInteractionType(),
      user.name || "Restorer"
    );
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion
    // Check if user is logged in
    const currentUser = CurrentSession.user;
    if (!currentUser || !currentUser?.id || !CurrentSession.isSignedIn) {
      enqueueSnackbar("You need to be logged in to encourage others", {
        variant: "warning",
        autoHideDuration: 3000,
      });
      return;
    }

    // Open the dialog to prompt for optional message
    setIsDialogOpen(true);
  };

  const handleSend = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    if (isInteracting) return; // Prevent double-clicks
    setIsInteracting(true);

    try {
      // Check if user is logged in
      const currentUser = CurrentSession.user;
      if (!currentUser || !currentUser?.id || !CurrentSession.isSignedIn) {
        enqueueSnackbar("You need to be logged in to encourage others", {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return;
      }

      // Prepare notification data
      const from = {
        uid: currentUser.id,
        username:
          currentUser.username?.display || currentUser.username?.standardized,
      };

      const to = {
        uid: user.id,
        username:
          user.username?.display || user.username?.standardized || user.name,
      };

      const type = getNotificationType(getInteractionType());
      const notificationType =
        type === "KUDOS" ? NOTIFICATION_TYPES.KUDOS : NOTIFICATION_TYPES.NUDGE;

      // Send notification with message
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await Notification.encourage(from, to, notificationType, message as any);
      AnalyticsService.event(`send_${notificationType}`, {
        has_message: !!message,
      });

      // Update state to show sent status
      setHasSentToday(true);

      // Show success toast
      enqueueSnackbar(getToastMessage(), {
        variant: "kudos",
        autoHideDuration: 3000,
      });

      // Trigger kudos recalculation if callback is provided
      if (onKudosSent) {
        onKudosSent(true);
      }

      // Close dialog and reset message
      setIsDialogOpen(false);
      setMessage("");
    } catch (error) {
      Logger.error("Failed to send notification:", error);
      enqueueSnackbar("Failed to send notification. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setIsInteracting(false);
    }
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    setMessage("");
  };

  const getButtonColor = ():
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning" => {
    if (hasSentToday) {
      return "success";
    }
    // Return primary as default since MUI doesn't support "purple" or "red" directly
    return "primary";
  };

  const getButtonVariant = (): "text" | "contained" | "outlined" => {
    return hasSentToday ? "contained" : "text";
  };

  const getButtonStyle = () => {
    if (hasSentToday) {
      const baseColor = "#f50057";

      return {
        backgroundColor: `${baseColor}20`, // 20% opacity
        color: baseColor,
        "&:hover": {
          backgroundColor: `${baseColor}30`, // 30% opacity on hover
        },
        "&:disabled": {
          backgroundColor: `${baseColor}15`, // 15% opacity when disabled
          color: baseColor,
        },
      };
    }
    const interactionType = getInteractionType();
    if (interactionType === "kudos" || interactionType === "reminder") {
      return { color: "#9c27b0" };
    }
    return { color: "#f44336" };
  };

  const button = (
    <Button
      onClick={handleClick}
      disabled={isInteracting || hasSentToday || notificationsDisabled}
      variant={getButtonVariant()}
      endIcon={getIcon()}
      color={getButtonColor()}
      sx={getButtonStyle()}
    >
      {getButtonText()}
    </Button>
  );

  const compactButton = (
    <Button
      onClick={(e) => !hasSentToday && handleClick(e)}
      disabled={isInteracting || hasSentToday || notificationsDisabled}
      variant="text"
      sx={{
        minWidth: "auto",
        width: 32,
        height: 32,
        borderRadius: "50%",
        padding: 0,
        color: hasSentToday
          ? lightGreen.A700
          : notificationsDisabled
          ? "#9e9e9e"
          : getInteractionType() === "kudos" ||
            getInteractionType() === "reminder"
          ? "#9c27b0"
          : "#f44336",
      }}
    >
      {getIcon(24)}
    </Button>
  );

  return (
    <>
      <Gatekeep featureName="Encourage Button">
        {compact ? compactButton : button}
      </Gatekeep>

      {/* Message Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {getEncourageDialogTitle(getInteractionType())}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={
              !currentUserAbove18 || !user?.above18
                ? "User has disabled messages"
                : "Optional personal note"
            }
            type="text"
            fullWidth
            disabled={!currentUserAbove18 || !user?.above18}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isInteracting) {
                handleSend();
              }
            }}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            color="primary"
            disabled={isInteracting}
            variant="contained"
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Kudos;
